// src/components/EditOrganisationLayer.jsx

import React, { useState, useEffect } from 'react';

import { useParams, useNavigate } from 'react-router-dom';
import {
    getOrganisationDataWithId,
    updateOrganisationWithId
} from '../utils/api/organisationAPI';

const ENTITY_TYPES = [
    'Private', 'Public', 'Non-profit', 'Government', 'Startup', 'Other'
];
const EMPLOYEE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '500+'];
const SECTOR_OPTIONS = [
    "Agriculture", "Automotive", "Aerospace", "Banking and Finance", "Construction",
    "Consumer Goods", "Energy", "Healthcare", "Information Technology", "Insurance",
    "Manufacturing", "Media and Entertainment", "Pharmaceuticals", "Retail",
    "Telecommunications", "Transportation and Logistics", "Real Estate", "Education",
    "Tourism and Hospitality", "Legal Services", "Government", "Non-Profit", "Utilities",
    "Technology", "Chemicals", "Food and Beverage", "Mining and Metals", "Textiles"
];

const EditOrganisationLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        status: true,
        entity_type: 'Private',
        noOfEmployees: '1-10',
        addressLine1: '',
        addressLine2: '',
        pincode: '',
        country: '',
        state: '',
        city: '',
        isSubscribed: true,

        gstOrPanNumber: '',
        sector: '',
    });

    // unified handler for text, number, select, and checkbox
    const handleChange = e => {
        const { name, type, value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]:
                type === 'checkbox'
                    ? checked
                    : type === 'number'
                        ? Number(value)
                        : value,
        }));
    };

    // fetch and populate existing organisation data
    useEffect(() => {
        if (!id) return;
        setIsLoading(true);
        getOrganisationDataWithId(id)
            .then(data => {
                const org = data.organization; 

                setFormData({
                    name: org.name || '',
                    status: org.status,
                    entity_type: org.entity_type || 'Private',
                    noOfEmployees: org.noOfEmployees || '1-10',
                    addressLine1: org.addressLine1 || '',
                    addressLine2: org.addressLine2 || '',
                    pincode: org.pincode ?? '',
                    country: org.country || '',
                    state: org.state || '',
                    city: org.city || '',
                    isSubscribed: org.isSubscribed,

                    gstOrPanNumber: org.gstOrPanNumber || '',
                    sector: org.sector || '',
                });
            })
            .catch(err => console.error('Failed to load organisation:', err))
            .finally(() => setIsLoading(false));
    }, [id]);

    const handleSubmit = async e => {
        e.preventDefault();
        setIsLoading(true);
        try {
           
            const res = await updateOrganisationWithId(id, formData, { setIsLoading });
      
            navigate(-1);
        } catch (err) {
            console.error('Update failed:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Common styles
    const cardStyle = {
        borderRadius: '12px',
        boxShadow: '0 4px 18px rgba(0, 0, 0, 0.08)',
        border: 'none',
        backgroundColor: '#fff'
    };
    
    const formLabelStyle = {
        fontSize: '14px',
        fontWeight: '600',
        color: '#444',
        marginBottom: '8px',
        display: 'block'
    };
    
    const inputStyle = {
        padding: '10px 16px',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        fontSize: '14px',
        width: '100%',
        backgroundColor: '#f9fafb'
    };
    
    const selectStyle = {
        ...inputStyle,
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23718096' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        paddingRight: '36px'
    };

    return (
        <div className="row gy-4">
            <div className="card h-100" style={cardStyle}>
                <div className="card-body p-24" style={{ padding: '32px' }}>
                    {/* Header with styling */}
                    <div style={{ 
                        borderBottom: '1px solid #f0f0f0', 
                        paddingBottom: '16px', 
                        marginBottom: '24px' 
                    }}>
                        <h2 style={{ 
                            fontSize: '22px', 
                            fontWeight: '600', 
                            color: '#333',
                            margin: '0 0 8px 0'
                         }}>
                            Edit Organisation
                        </h2>
                        <p style={{ 
                            fontSize: '14px',
                            color: '#666',
                            margin: 0
                        }}>
                            Update organisation details and preferences
                        </p>
                    </div>

                    <div className="tab-content">
                        <div className="tab-pane fade show active">
                            <form onSubmit={handleSubmit}>
                                {isLoading && (
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: '#e8f4ff',
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                        color: '#0d6efd',
                                        marginBottom: '20px'
                                    }}>
                                        <p style={{ margin: 0 }}>Loading data...</p>
                                    </div>
                                )}

                                <div className="row">
                                    {/* Name */}
                                    <div className="col-sm-6 mb-20">
                                        <label style={formLabelStyle}>
                                            Name <span style={{ color: '#dc3545' }}>*</span>
                                        </label>
                                        <input
                                            name="name"
                                            type="text"
                                            style={inputStyle}
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Organisation Name"
                                            required
                                        />
                                    </div>

                                    {/* Status */}
                                    <div className="col-sm-6 mb-20">
                                        <label style={formLabelStyle}>
                                            Status
                                        </label>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '12px',
                                            backgroundColor: '#f9fafb',
                                            padding: '8px 16px',
                                            borderRadius: '8px',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <span style={{ 
                                                fontSize: '14px', 
                                                fontWeight: '500',
                                                color: formData.status ? '#0d6efd' : '#6c757d',
                                            }}>
                                                {formData.status ? 'Active' : 'Inactive'}
                                            </span>
                                            <div style={{ flexGrow: 1 }}></div>
                                            <label style={{ 
                                                position: 'relative', 
                                                display: 'inline-block', 
                                                width: '46px', 
                                                height: '24px', 
                                                margin: '0' 
                                            }}>
                                                <input
                                                    name="status"
                                                    type="checkbox"
                                                    checked={formData.status}
                                                    onChange={handleChange}
                                                    style={{
                                                        opacity: '0',
                                                        width: '0',
                                                        height: '0'
                                                    }}
                                                />
                                                <span style={{
                                                    position: 'absolute',
                                                    cursor: 'pointer',
                                                    top: '0',
                                                    left: '0',
                                                    right: '0',
                                                    bottom: '0',
                                                    backgroundColor: formData.status ? '#0d6efd' : '#e2e2e2',
                                                    transition: 'background-color 0.3s ease',
                                                    borderRadius: '34px',
                                                    border: '1px solid ' + (formData.status ? '#0d6efd' : '#ccc')
                                                }}></span>
                                                <span style={{
                                                    position: 'absolute',
                                                    content: '""',
                                                    height: '18px',
                                                    width: '18px',
                                                    left: formData.status ? '24px' : '4px',
                                                    bottom: '3px',
                                                    backgroundColor: 'white',
                                                    transition: '0.3s',
                                                    borderRadius: '50%',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                }}></span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Entity Type */}
                                    <div className="col-sm-6 mb-20">
                                        <label style={formLabelStyle}>
                                            Entity Type
                                        </label>
                                        <select
                                            name="entity_type"
                                            style={selectStyle}
                                            value={formData.entity_type}
                                            onChange={handleChange}
                                        >
                                            {ENTITY_TYPES.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* No. of Employees */}
                                    <div className="col-sm-6 mb-20">
                                        <label style={formLabelStyle}>
                                            No. of Employees
                                        </label>
                                        <select
                                            name="noOfEmployees"
                                            style={selectStyle}
                                            value={formData.noOfEmployees}
                                            onChange={handleChange}
                                        >
                                            {EMPLOYEE_OPTIONS.map(opt => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Section Divider - Address */}
                                    <div className="col-12 mb-16">
                                        <div style={{ 
                                            borderBottom: '1px solid #f0f0f0', 
                                            margin: '8px 0 16px',
                                            position: 'relative'
                                        }}>
                                            <span style={{
                                                position: 'absolute',
                                                top: '-12px',
                                                backgroundColor: '#fff',
                                                padding: '0 12px',
                                                fontSize: '14px',
                                                color: '#6c757d',
                                                fontWeight: '500',
                                                left: '16px'
                                            }}>Address Information</span>
                                        </div>
                                    </div>

                                    {/* Address Line 1 */}
                                    <div className="col-sm-6 mb-20">
                                        <label style={formLabelStyle}>
                                            Address Line 1
                                        </label>
                                        <input
                                            name="addressLine1"
                                            type="text"
                                            style={inputStyle}
                                            value={formData.addressLine1}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Address Line 2 */}
                                    <div className="col-sm-6 mb-20">
                                        <label style={formLabelStyle}>
                                            Address Line 2
                                        </label>
                                        <input
                                            name="addressLine2"
                                            type="text"
                                            style={inputStyle}
                                            value={formData.addressLine2}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Pincode */}
                                    <div className="col-sm-4 mb-20">
                                        <label style={formLabelStyle}>
                                            Pincode
                                        </label>
                                        <input
                                            name="pincode"
                                            type="number"
                                            style={inputStyle}
                                            value={formData.pincode}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Country */}
                                    <div className="col-sm-4 mb-20">
                                        <label style={formLabelStyle}>
                                            Country
                                        </label>
                                        <input
                                            name="country"
                                            type="text"
                                            style={inputStyle}
                                            value={formData.country}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* State */}
                                    <div className="col-sm-4 mb-20">
                                        <label style={formLabelStyle}>
                                            State
                                        </label>
                                        <input
                                            name="state"
                                            type="text"
                                            style={inputStyle}
                                            value={formData.state}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* City */}
                                    <div className="col-sm-6 mb-20">
                                        <label style={formLabelStyle}>
                                            City
                                        </label>
                                        <input
                                            name="city"
                                            type="text"
                                            style={inputStyle}
                                            value={formData.city}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Section Divider - Subscription */}
                                    <div className="col-12 mb-16">
                                        <div style={{ 
                                            borderBottom: '1px solid #f0f0f0', 
                                            margin: '8px 0 16px',
                                            position: 'relative'
                                        }}>
                                            <span style={{
                                                position: 'absolute',
                                                top: '-12px',
                                                backgroundColor: '#fff',
                                                padding: '0 12px',
                                                fontSize: '14px',
                                                color: '#6c757d',
                                                fontWeight: '500',
                                                left: '16px'
                                            }}>Subscription Details</span>
                                        </div>
                                    </div>

                                    {/* Subscribed */}
                                    <div className="col-sm-6 mb-20">
                                        <label style={formLabelStyle}>
                                            Subscription Status
                                        </label>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '12px',
                                            backgroundColor: '#f9fafb',
                                            padding: '8px 16px',
                                            borderRadius: '8px',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <span style={{ 
                                                fontSize: '14px', 
                                                fontWeight: '500',
                                                color: formData.isSubscribed ? '#198754' : '#6c757d',
                                            }}>
                                                {formData.isSubscribed ? 'Subscribed' : 'Not Subscribed'}
                                            </span>
                                            <div style={{ flexGrow: 1 }}></div>
                                            <label style={{ 
                                                position: 'relative', 
                                                display: 'inline-block', 
                                                width: '46px', 
                                                height: '24px', 
                                                margin: '0' 
                                            }}>
                                                <input
                                                    name="isSubscribed"
                                                    type="checkbox"
                                                    checked={formData.isSubscribed}
                                                    onChange={handleChange}
                                                    style={{
                                                        opacity: '0',
                                                        width: '0',
                                                        height: '0'
                                                    }}
                                                />
                                                <span style={{
                                                    position: 'absolute',
                                                    cursor: 'pointer',
                                                    top: '0',
                                                    left: '0',
                                                    right: '0',
                                                    bottom: '0',
                                                    backgroundColor: formData.isSubscribed ? '#198754' : '#e2e2e2',
                                                    transition: 'background-color 0.3s ease',
                                                    borderRadius: '34px',
                                                    border: '1px solid ' + (formData.isSubscribed ? '#198754' : '#ccc')
                                                }}></span>
                                                <span style={{
                                                    position: 'absolute',
                                                    content: '""',
                                                    height: '18px',
                                                    width: '18px',
                                                    left: formData.isSubscribed ? '24px' : '4px',
                                                    bottom: '3px',
                                                    backgroundColor: 'white',
                                                    transition: '0.3s',
                                                    borderRadius: '50%',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                }}></span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Minutes Used */}
                                    {/* <div className="col-sm-6 mb-20">
                                        <label style={formLabelStyle}>
                                            Minutes Used
                                        </label>
                                        <input
                                            name="minutesUsed"
                                            type="number"
                                            style={{
                                                ...inputStyle,
                                                backgroundColor: formData.isSubscribed ? '#f9fafb' : '#f5f5f5',
                                                color: formData.isSubscribed ? '#212529' : '#6c757d'
                                            }}
                                            value={formData.minutesUsed}
                                            onChange={handleChange}
                                        />
                                    </div> */}

                                    {/* Section Divider - Additional Info */}
                                    <div className="col-12 mb-16">
                                        <div style={{ 
                                            borderBottom: '1px solid #f0f0f0', 
                                            margin: '8px 0 16px',
                                            position: 'relative'
                                        }}>
                                            <span style={{
                                                position: 'absolute',
                                                top: '-12px',
                                                backgroundColor: '#fff',
                                                padding: '0 12px',
                                                fontSize: '14px',
                                                color: '#6c757d',
                                                fontWeight: '500',
                                                left: '16px'
                                            }}>Additional Information</span>
                                        </div>
                                    </div>

                                    {/* GST / PAN */}
                                    <div className="col-sm-6 mb-20">
                                        <label style={formLabelStyle}>
                                            GST / PAN Number
                                        </label>
                                        <input
                                            name="gstOrPanNumber"
                                            type="text"
                                            style={inputStyle}
                                            value={formData.gstOrPanNumber}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Sector */}
                                    <div className="col-sm-6 mb-20">
                                        <label style={formLabelStyle}>
                                            Sector <span style={{ color: '#dc3545' }}>*</span>
                                        </label>
                                        <select
                                            name="sector"
                                            style={selectStyle}
                                            value={formData.sector}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="" disabled>Select Sector</option>
                                            {SECTOR_OPTIONS.map(sec => (
                                                <option key={sec} value={sec}>{sec}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    gap: '16px', 
                                    marginTop: '32px',
                                    padding: '16px 0 0',
                                    borderTop: '1px solid #f0f0f0'
                                }}>
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        style={{
                                            padding: '10px 32px',
                                            borderRadius: '8px',
                                            border: '1px solid #dc3545',
                                            color: '#dc3545',
                                            backgroundColor: 'transparent',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fff1f1'}
                                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        style={{
                                            padding: '10px 32px',
                                            borderRadius: '8px',
                                            border: '1px solid #0d6efd',
                                            color: '#fff',
                                            backgroundColor: '#0d6efd',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            opacity: isLoading ? 0.7 : 1,
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseOver={(e) => {
                                            if (!isLoading) e.currentTarget.style.backgroundColor = '#0b5ed7';
                                        }}
                                        onMouseOut={(e) => {
                                            if (!isLoading) e.currentTarget.style.backgroundColor = '#0d6efd';
                                        }}
                                    >
                                        {isLoading ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditOrganisationLayer;